import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Circle,
  Container,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import Title from "@components/title";
import AnimatedPage from "@components/animated-page";
import Table from "@components/table";
import { FiFile } from "react-icons/fi";
import { CellProps, Column } from "react-table";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { BREAKPOINT_MD } from "@utils/constants/breakpoints";
import { useAppSelector } from "@store/hooks";
import { AppDispatch, RootState } from "@store/index";
import Spinner from "@components/spinner";
import { unwrapResult } from "@reduxjs/toolkit";
import { getPrintJobs } from "@features/print/action";
import { useDispatch } from "react-redux";
import { printJobsRes, printJobsTable } from "@features/print/types";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import fileColor from "@assets/images/file-color.svg";
import fileBlackWhite from "@assets/images/file-black-white.svg";
import i18n from "i18next";
import useTitle from "@hooks/useTitle";

const FileNameCell: React.FC<{ name: string }> = ({ name }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Text
      minW={"200px"}
      maxW={"200px"}
      noOfLines={expanded ? undefined : 1}
      whiteSpace={expanded ? "normal" : undefined}
      wordBreak={expanded ? "break-all" : undefined}
      cursor={"pointer"}
      onClick={() => setExpanded((v) => !v)}
    >
      {name}
    </Text>
  );
};

const RecentePrints = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, printJobs } = useAppSelector(
    (state: RootState) => state.print
  );
  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const lang = i18n.language;

  useTitle(t(("documentTitle.printManagement")))

  const [selectedRows, setSelectedData] = useState<printJobsRes[]>([]);
  const onSelectedRows = (rows: printJobsRes[]) => {
    const mappedRows = rows.map((r) => r);
    if (!selectedRows) {
      setSelectedData([...selectedRows, ...mappedRows]);
    }
  };

  useEffect(() => {
    (async () => {
      const data = unwrapResult(await dispatch(getPrintJobs()));
      return data.length ? data.filter((el: printJobsRes) => {
        return el.STATUS === 3;
      }) : [];
    })();
  }, [dispatch]);

  const data: printJobsRes[] = useMemo(
    () =>
      printJobs.length ? printJobs.filter((el: printJobsRes) => {
        return el.STATUS === 3;
      }) : [],
    [printJobs]
  );

  const columns: Array<Column<printJobsTable>> = useMemo(
    () => [
      {
        Header: t("printManagement.recentPrints.table.headingRow.file"),
        accessor: ({ DOCUMENT_NAME, PAGES }) => [DOCUMENT_NAME, PAGES],
        Cell: ({ value }: CellProps<printJobsRes>) => {
          const [DOCUMENT_NAME, PRINT_COPIES] = value;
          return (
            <>
              <HStack spacing={"12px"}>
                <Circle
                  size={"40px"}
                  bg={"custom.brand_primary"}
                  color={"custom.brand_primaryLight"}
                >
                  <Icon as={FiFile} />
                </Circle>
                <VStack alignItems={"flex-start"}>
                  <FileNameCell name={DOCUMENT_NAME ?? ""} />
                  <Text>
                    {PRINT_COPIES === 1
                      ? `${PRINT_COPIES} ${t(
                          "printManagement.recentPrints.table.body.printCopies.page"
                        )}`
                      : `${PRINT_COPIES} ${t(
                          "printManagement.recentPrints.table.body.printCopies.pages"
                        )}`}
                  </Text>
                </VStack>
              </HStack>
            </>
          );
        },
      },
      {
        Header: t("printManagement.recentPrints.table.headingRow.type"),
        id: "PRINT_COLOR",
        accessor: ({ PRINT_COLOR }) => [PRINT_COLOR],
        Cell: ({ value }: CellProps<printJobsRes>) => {
          const [PRINT_COLOR] = value;
          return (
            <>
              <HStack spacing={"12px"}>
                <VStack alignItems={"flex-start"}>
                  <Text>
                    {PRINT_COLOR === 0
                      ? t(
                          "printManagement.recentPrints.table.body.printColor.color"
                        )
                      : t(
                          "printManagement.recentPrints.table.body.printColor.black-white"
                        )}
                  </Text>
                </VStack>
              </HStack>
            </>
          );
        },
        show: width > BREAKPOINT_MD,
      },
      {
        Header: t("printManagement.recentPrints.table.headingRow.properties"),
        id: "eigenschappen",
        accessor: ({ PRINT_COLOR, PAGESIZE, PRINT_COPIES }) => [
          PRINT_COLOR,
          PAGESIZE,
          PRINT_COPIES,
        ],
        Cell: ({ value }: CellProps<printJobsRes>) => {
          const [PRINT_COLOR, PAGESIZE, PRINT_COPIES] = value;
          return (
            <>
              <HStack spacing={"12px"}>
                <VStack alignItems={"flex-start"}>
                  <HStack>
                    {PRINT_COLOR === 0 ? (
                      <>
                        <Image
                          src={fileColor}
                          alt={"file-color"}
                          maxW={"14px"}
                          w={"full"}
                        />
                      </>
                    ) : (
                      <>
                        <Image
                          src={fileBlackWhite}
                          alt={"file-black-white"}
                          maxW={"14px"}
                          w={"full"}
                        />
                      </>
                    )}
                    <Text>
                      {PAGESIZE === 0
                        ? t(
                            "printManagement.recentPrints.table.body.pageSize.a4"
                          )
                        : t(
                            "printManagement.recentPrints.table.body.pageSize.a3"
                          )}
                    </Text>
                  </HStack>
                  <Text>{`${PRINT_COPIES} ${
                    PRINT_COPIES === 1
                      ? t(
                          "printManagement.recentPrints.table.body.printCopies.print"
                        )
                      : t(
                          "printManagement.recentPrints.table.body.printCopies.prints"
                        )
                  }`}</Text>
                </VStack>
              </HStack>
            </>
          );
        },
        show: width < BREAKPOINT_MD,
      },
      {
        Header: t("printManagement.recentPrints.table.headingRow.printFormat"),
        id: "afdrukformaat",
        accessor: ({ PAGESIZE }) => [PAGESIZE],
        Cell: ({ value }: CellProps<printJobsRes>) => {
          const [PAGESIZE] = value;
          return (
            <>
              <HStack spacing={"12px"}>
                <VStack alignItems={"flex-start"}>
                  <Text>
                    {PAGESIZE === 0
                      ? t("printManagement.recentPrints.table.body.pageSize.a4")
                      : t(
                          "printManagement.recentPrints.table.body.pageSize.a3"
                        )}
                  </Text>
                </VStack>
              </HStack>
            </>
          );
        },
        show: width > BREAKPOINT_MD,
      },
      {
        Header: t("printManagement.recentPrints.table.headingRow.printCopies"),
        accessor: "PRINT_COPIES",
        show: width > BREAKPOINT_MD,
      },
    ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [width, t]
  );

  if (isLoading) return <Spinner />;
  return (
    <>
      <Box>
        <Title title={t("printManagement.recentPrints.title")} />
        <AnimatedPage animationType={"FadeInUp"}>
          <Container
            maxW={"container.xl"}
            p={{ base: "22px 16px", md: "45px 32px" }}
          >
            <Table
              data={data}
              columns={columns}
              onSelectedRows={onSelectedRows}
              managePrintsRedirectUrl={pathname}
              newPrintButton={false}
              showManageDelete={false}
            />
          </Container>
        </AnimatedPage>
      </Box>
    </>
  );
};

export default RecentePrints;
